import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import api from '../services/api';
import Coin from '../components/Coin';

const MyBets = () => {
  const { user } = useAuthStore();
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      fetchMyBets();
    }
  }, [user]);

  const fetchMyBets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bets/my-bets');
      // Always ensure bets is an array
      setBets(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError('Erreur lors du chargement de vos paris');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBetStatusBadge = (status) => {
    const statusConfig = {
      'pending': { text: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      'won': { text: 'Gagné', color: 'bg-green-100 text-green-800' },
      'lost': { text: 'Perdu', color: 'bg-red-100 text-red-800' },
      'cancelled': { text: 'Annulé', color: 'bg-gray-100 text-gray-800' }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Veuillez vous connecter pour voir vos paris</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de vos paris...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={fetchMyBets}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Mes Paris
          </h1>
          <p className="text-gray-600">
            Historique de tous vos paris et leurs résultats
          </p>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <span className="text-blue-600 text-xl">📊</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total des paris</p>
                <p className="text-2xl font-bold text-gray-900">{bets.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <span className="text-green-600 text-xl">✅</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Paris gagnés</p>
                <p className="text-2xl font-bold text-gray-900">
                  {bets.filter(bet => bet.status === 'won').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <span className="text-red-600 text-xl">❌</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Paris perdus</p>
                <p className="text-2xl font-bold text-gray-900">
                  {bets.filter(bet => bet.status === 'lost').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <span className="text-yellow-600 text-xl">⏳</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En attente</p>
                <p className="text-2xl font-bold text-gray-900">
                  {bets.filter(bet => bet.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des paris */}
        {bets.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-gray-400 text-6xl mb-4">🎲</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              Aucun pari trouvé
            </h3>
            <p className="text-gray-600 mb-6">
              Vous n'avez pas encore placé de paris. Commencez par explorer les combats disponibles !
            </p>
            <Link
              to="/combats"
              className="inline-block bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600"
            >
              Voir les combats
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Historique des paris
              </h2>
            </div>
            
            <div className="divide-y divide-gray-200">
              {bets.map((bet) => (
                <div key={bet.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {bet.combat?.title || `Combat #${bet.combatId}`}
                        </h3>
                        <div className="flex items-center space-x-4">
                          {getBetStatusBadge(bet.status)}
                          <span className="text-sm text-gray-500">
                            {formatDate(bet.createdAt)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Votre prédiction:</span>
                          <span className="ml-2">{bet.predictedWinner}</span>
                        </div>
                        <div>
                          <span className="font-medium">Montant parié:</span>
                          <span className="ml-2 font-semibold text-orange-600">
                            <Coin amount={bet.amount} symbol="HTG" />
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Gagnant final:</span>
                          <span className="ml-2">
                            {bet.combat?.winner || 'Non déterminé'}
                          </span>
                        </div>
                      </div>

                      {bet.status === 'won' && bet.winnings && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center text-green-700">
                            <span className="mr-2">💰</span>
                            <span className="font-medium">
                              Gains: <Coin amount={bet.winnings} symbol="HTG" />
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="ml-6">
                      <Link
                        to={`/combats/${bet.combatId}`}
                        className="text-orange-600 hover:text-orange-700 font-medium"
                      >
                        Voir le combat →
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBets; 